import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CreditCheckoutView from '../components/CreditSystem/CreditCheckoutView';
import { DEFAULT_CREDIT_CHECKOUT_PLAN, readPlanIdFromUrl, resolvePackFromId } from '../components/CreditSystem/creditCheckoutUtils';
import { useCreditsCheckout } from '../components/CreditSystem/useCreditsCheckout';
import { useAuthentication } from '../hooks/useAuthentication';
import { PlanStatusProvider, usePlanStatus } from '../hooks/usePlanStatus';
import { getNextHigherPack, isHigherTierPack, isSamePack } from '../utils/planUpgrade';
import LoadingSpinner from '../components/LoadingSpinner';
import { goBackFromCheckout, goToCreditCheckout, goToPaymentSuccess } from '../utils/creditCheckoutNavigation';
import { SEO } from '../shared/components/SEO';

interface CreditsCheckoutPageProps {
  onNavigate?: (view: string) => void;
}

function resolveInitialCheckoutPlan(requestedPlanId: string | undefined, currentPlanId: string | null): string {
  const requested = resolvePackFromId(requestedPlanId)?.id;
  if (!requested) return DEFAULT_CREDIT_CHECKOUT_PLAN;
  if (!currentPlanId) return requested;
  if (isSamePack(requested, currentPlanId)) {
    return getNextHigherPack(currentPlanId) ?? requested;
  }
  if (isHigherTierPack(requested, currentPlanId)) return requested;
  return getNextHigherPack(currentPlanId) ?? requested;
}

const CreditsCheckoutPageInner: React.FC<CreditsCheckoutPageProps> = () => {
  const { t, i18n } = useTranslation();
  const { session, loading: authLoading } = useAuthentication();
  const planStatus = usePlanStatus();
  const urlPlanId = readPlanIdFromUrl();
  const initialPackId = useMemo(
    () => (planStatus.loading
      ? (urlPlanId ?? DEFAULT_CREDIT_CHECKOUT_PLAN)
      : resolveInitialCheckoutPlan(urlPlanId, planStatus.planId)),
    [planStatus.loading, planStatus.planId, urlPlanId],
  );

  const checkout = useCreditsCheckout({
    initialPackId,
    onPaymentSuccess: (orderId) => {
      goToPaymentSuccess(orderId);
    },
  });

  useEffect(() => {
    if (authLoading) return;
    if (session) return;

    const returnPath = `${window.location.pathname}${window.location.search}`;
    sessionStorage.setItem('redirect_after_login', returnPath);
    window.location.replace('/login');
  }, [authLoading, session]);

  useEffect(() => {
    if (!session || !checkout.ready || checkout.selectedPlan || checkout.paymentFailure) return;
    goToCreditCheckout(DEFAULT_CREDIT_CHECKOUT_PLAN);
  }, [session, checkout.ready, checkout.selectedPlan, checkout.paymentFailure]);

  const handleBack = () => {
    goBackFromCheckout();
  };

  if (authLoading || !session) {
    return (
      <div className="credit-checkout-page credit-checkout-page--loading">
        <LoadingSpinner size="md" text="" />
      </div>
    );
  }

  if (!checkout.ready || planStatus.loading) {
    return (
      <div className="credit-checkout-page credit-checkout-page--loading">
        <LoadingSpinner size="md" text="" />
      </div>
    );
  }

  return (
    <div key={i18n.language} className="credit-checkout-page">
      <SEO
        title={t('creditsPurchaseModal.seoTitle', 'Checkout — Buy Credits | GoGrabJob')}
        description={t(
          'creditsPurchaseModal.seoDescription',
          'Purchase GoGrabJob credits securely. Choose a plan and complete checkout with Cashfree.',
        )}
        canonical="https://www.gograbjob.com/checkout"
        noIndex
      />
      <div className="credit-checkout-shell">
        <CreditCheckoutView {...checkout} onBack={handleBack} />
      </div>
    </div>
  );
};

const CreditsCheckoutPage: React.FC<CreditsCheckoutPageProps> = (props) => (
  <PlanStatusProvider>
    <CreditsCheckoutPageInner {...props} />
  </PlanStatusProvider>
);

export default CreditsCheckoutPage;
